import { Box, Paper, Typography, Chip, Badge, IconButton, Tooltip } from '@mui/material';
import { Lock, CheckCircle, Description } from '@mui/icons-material';
import { WorkflowPhase, WorkflowTask } from '../../types';
import { TaskCard } from './TaskCard';

interface PhaseCardProps {
  phase: WorkflowPhase;
  isActive: boolean;
  isLocked: boolean;
  onTaskStart: (task: WorkflowTask) => void;
  onTaskViewDetails: (task: WorkflowTask) => void;
  onDocumentView?: (folderPath?: string) => void;
  documentCount?: number;
}

export const PhaseCard = ({ phase, isActive, isLocked, onTaskStart, onTaskViewDetails, onDocumentView, documentCount = 0 }: PhaseCardProps) => {
  const getStatusColor = () => {
    if (phase.status === 'COMPLETE') return 'success';
    if (phase.status === 'CANCELLED') return 'error';
    if (isActive) return 'primary';
    return 'default';
  };

  const getStatusLabel = () => {
    if (isActive) return 'ACTIVE';
    if (isLocked) return 'DISABLED';
    return phase.status;
  };

  return (
    <Paper 
      sx={{ 
        p: 2, 
        mb: 2,
        border: isActive ? 2 : 1,
        borderColor: isActive ? 'primary.main' : 'divider',
        opacity: isLocked ? 0.6 : 1
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          PHASE {phase.displayOrder}: {phase.name}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {onDocumentView && (
          <Tooltip title="View documents">
            <Badge
              badgeContent={documentCount}
              color="primary"
              sx={{ mr: 1 }}
            >
              <IconButton
                size="small"
                onClick={() => onDocumentView(`/Phase${phase.phaseId}`)}
                sx={{ cursor: 'pointer' }}
              >
                <Description fontSize="small" />
              </IconButton>
            </Badge>
          </Tooltip>
        )}
        <Chip 
          label={getStatusLabel()}
          color={getStatusColor() as 'success' | 'error' | 'primary' | 'default'}
          icon={isLocked ? <Lock /> : phase.status === 'COMPLETE' ? <CheckCircle /> : undefined}
          size="small"
        />
      </Box>

      {phase.tasks.map((task) => (
        <TaskCard
          key={task.taskId}
          task={task}
          taskInstance={task.taskInstance}
          isLocked={isLocked}
          onStart={() => onTaskStart(task)}
          onViewDetails={() => onTaskViewDetails(task)}
          onDocumentView={onDocumentView}
        />
      ))}
    </Paper>
  );
};

